import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApiClient } from "../service/API";
import { logoutUser } from "../util/APIUtils";

/** Normalize backend field variants into a single shape */
/** Normalize backend field variants into a single shape */
function canonicalizeTicket(t) {
  if (!t) return t;

  // date variants
  const ETDETADate = t.etdetadate ?? t.ETDETADate ?? t.etdEtaDate ?? null;
  const SOBATADate = t.sobatadate ?? t.SOBATADate ?? t.sobAtaDate ?? null;

  // urgent variants (from native query/projection vs entity DTO)
  const urgentStatus =
    t.urgentStatus ?? t.urgent_status ?? t.urgentstatus ?? null;

  const urgentStage =
    t.urgentStage ?? t.urgent_stage ?? t.urgentstage ?? null;

  const urgentApprovedDatetime =
    t.urgentApprovedDatetime ??
    t.urgent_approved_datetime ??
    t.urgentApprovedDateTime ??
    null;

  // selectedStageName can be:
  // - array of strings ✅
  // - JSON string '["A","B"]' ❌
  // - array with 1 JSON string ['["A","B"]'] ❌
  let selectedStageName = t.selectedStageName ?? t.selectedStageNameJson ?? null;

  if (typeof selectedStageName === "string") {
    const s = selectedStageName.trim();
    if (s.startsWith("[") && s.endsWith("]")) {
      try { selectedStageName = JSON.parse(s); } catch { /* ignore */ }
    }
  } else if (
    Array.isArray(selectedStageName) &&
    selectedStageName.length === 1 &&
    typeof selectedStageName[0] === "string" &&
    selectedStageName[0].trim().startsWith("[")
  ) {
    try { selectedStageName = JSON.parse(selectedStageName[0]); } catch { /* ignore */ }
  }

  return {
    ...t,
    ETDETADate,
    SOBATADate,
    urgentStatus,
    urgentStage,
    urgentApprovedDatetime,
    selectedStageName,
  };
}

/** Stable “version” so we only apply newer data */
function versionOf(t) {
  if (!t) return null;
  const contentSig = (() => {
    try {
      return JSON.stringify({
        id: t.id,
        status: t.status,
        stage: t.stage,
        ETDETADate: t.ETDETADate ?? null,
        SOBATADate: t.SOBATADate ?? null,
        sysFreightRef: t.sysFreightRef ?? null,
        bookingRef: t.bookingRef ?? null,
        assignedTo: t.assignedTo ?? null,
        serviceType: t.serviceType ?? null,
        remark: t.remark ?? null,
        quotation: t.quotation ?? null,
        // Compare document identity/metadata, not only the number of files.
        docs: Array.isArray(t.docs)
          ? t.docs.map(doc => ({
              fileId: doc?.fileId ?? null,
              name: doc?.name ?? null,
              type: doc?.type ?? null,
            }))
          : [],
        folderName: t.folderName ?? null,
        deliveryAgent: t.deliveryAgent ?? null,
        selectedStageName: Array.isArray(t.selectedStageName)
          ? t.selectedStageName.join("|")
          : t.selectedStageName ?? null,
      });
    } catch {
      // Last resort: something changed for sure
      return String(Date.now());
    }
  })();

  // Always include stamp *and* content—this guards against stale updatedAt/version
  const stamp = t.updatedAt ?? t.version ?? null;
  return JSON.stringify({ stamp, contentSig });
}

function normalizeTicketId(id) {
  return id == null ? null : String(id);
}

function belongsToTicket(ticket, id) {
  return id != null && ticket?.id != null &&
    normalizeTicketId(ticket.id) === normalizeTicketId(id);
}

/**
 * One ticket snapshot per route.
 * Route changes reset the snapshot and invalidate old requests/callbacks.
 * Same-ticket refreshes keep the current snapshot visible while loading.
 */
export function useTicket({ ticketId, seed, wsMessage, apiClient: apiClientProp }) {
  const apiFromHook = useApiClient();
  const apiRef = useRef(apiClientProp ?? apiFromHook);
  useEffect(() => {
    apiRef.current = apiClientProp ?? apiFromHook;
  }, [apiClientProp, apiFromHook]);

  const routeId = normalizeTicketId(ticketId);
  // A new scope is needed even for A -> B -> A, so old A callbacks stay stale.
  const scope = useMemo(() => ({ ticketId: routeId }), [routeId]);
  const initialTicket = useMemo(
    () => belongsToTicket(seed, routeId) ? canonicalizeTicket(seed) : null,
    [seed, routeId]
  );
  const seedRef = useRef(initialTicket);
  useEffect(() => { seedRef.current = initialTicket; }, [initialTicket]);

  const [state, setState] = useState(() => ({
    scope,
    data: initialTicket,
    loading: Boolean(routeId && !initialTicket),
    refreshing: false,
    error: null,
  }));

  const activeScopeRef = useRef(null);
  const dataRef = useRef(initialTicket);
  const lastVersionRef = useRef(versionOf(initialTicket));
  const reqSeqRef = useRef(0);
  const abortRef = useRef(null);
  const lastRefetchTsRef = useRef(0);
  const lastHandledWsKeyRef = useRef(null);

  // Legacy name retained for callers. This deduplicates identical snapshots;
  // it is not a chronological comparison of backend version numbers.
  const applyIfNewer = useCallback((raw) => {
    if (activeScopeRef.current !== scope || !raw) return;
    if (routeId != null && !belongsToTicket(raw, routeId)) return;

    const fresh = canonicalizeTicket(raw);
    const nextVersion = versionOf(fresh);
    if (nextVersion === lastVersionRef.current) return;

    lastVersionRef.current = nextVersion;
    dataRef.current = fresh;
    setState(prev => prev.scope === scope ? { ...prev, data: fresh } : prev);
  }, [scope, routeId]);

  const refetch = useCallback(
    async (id = routeId, { background = false } = {}) => {
      const requestedId = normalizeTicketId(id);
      if (!requestedId || requestedId !== routeId || activeScopeRef.current !== scope) return;

      abortRef.current?.abort();
      const mySeq = ++reqSeqRef.current;
      const ac = new AbortController();
      abortRef.current = ac;
      const isCurrentRequest = () =>
        activeScopeRef.current === scope &&
        mySeq === reqSeqRef.current &&
        !ac.signal.aborted;

      const hasCurrentData = belongsToTicket(dataRef.current, requestedId);
      setState(prev => prev.scope === scope ? {
        ...prev,
        loading: !hasCurrentData,
        refreshing: background || hasCurrentData,
        error: null,
      } : prev);

      try {
        const res = await apiRef.current.get(`/tickets/${requestedId}`, { signal: ac.signal });
        if (!isCurrentRequest()) return;
        if (!belongsToTicket(res.data, requestedId)) {
          throw new Error('The server returned data for a different or missing ticket ID.');
        }
        applyIfNewer(res.data);
      } catch (error) {
        if (isCurrentRequest()) {
          setState(prev => prev.scope === scope ? { ...prev, error } : prev);
        }
      } finally {
        if (isCurrentRequest()) {
          abortRef.current = null;
          setState(prev => prev.scope === scope
            ? { ...prev, loading: false, refreshing: false }
            : prev);
        }
      }
    },
    [scope, routeId, applyIfNewer]
  );

  // Reset on every route change, not only the first mount.
  // A ref supplies the seed without making a seed update restart the request.
  useEffect(() => {
    activeScopeRef.current = scope;
    ++reqSeqRef.current;
    abortRef.current?.abort();
    abortRef.current = null;

    const next = seedRef.current;
    dataRef.current = next;
    lastVersionRef.current = versionOf(next);
    lastRefetchTsRef.current = 0;
    lastHandledWsKeyRef.current = null;
    setState({
      scope,
      data: next,
      loading: Boolean(routeId && !next),
      refreshing: false,
      error: null,
    });

    if (routeId) void refetch(routeId, { background: Boolean(next) });

    return () => {
      activeScopeRef.current = null;
      ++reqSeqRef.current;
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [scope, routeId, refetch]);

  // Accept a late matching seed only while there is no snapshot. A navigation
  // seed must not overwrite an already loaded network/local-update snapshot.
  useEffect(() => {
    if (initialTicket && activeScopeRef.current === scope && !dataRef.current) {
      applyIfNewer(initialTicket);
    }
  }, [scope, initialTicket, applyIfNewer]);

  // Debounce bursts and keep at least 500 ms between WS-triggered fetches.
  // Mark the event handled when the timer fires, not before its cleanup.
  useEffect(() => {
    if (!routeId || wsMessage?.type !== 'REFRESH') return;
    if (wsMessage.ticketId != null && normalizeTicketId(wsMessage.ticketId) !== routeId) return;

    const wsKey = wsMessage.id ?? wsMessage.ts ?? wsMessage.timestamp ?? JSON.stringify(wsMessage);
    if (wsKey === lastHandledWsKeyRef.current) return;
    const delay = Math.max(150, 500 - (Date.now() - lastRefetchTsRef.current));
    const timer = setTimeout(() => {
      if (activeScopeRef.current !== scope) return;
      lastHandledWsKeyRef.current = wsKey;
      lastRefetchTsRef.current = Date.now();
      void refetch(routeId, { background: true });
    }, delay);
    return () => clearTimeout(timer);
  }, [scope, routeId, wsMessage, refetch]);

  // Effects run after render. Do not expose the previous ticket even during
  // the first render of a new route, before the reset effect has executed.
  const visible = state.scope === scope ? state : {
    data: initialTicket,
    loading: Boolean(routeId && !initialTicket),
    refreshing: false,
    error: null,
  };
  return {
    data: visible.data,
    loading: visible.loading,
    refreshing: visible.refreshing,
    error: visible.error,
    refetch,
    applyIfNewer,
  };
}

/**
 * Table data hook (kept for completeness).
 * Uses a stable apiRef to avoid loops if provider recreates clients.
 */
const useTicketsTableData = (
  filters,
  pageSize,
  currentPage,
  setMainRefresh,
  executeFilter,
  setExecuteFilter
) => {
  const apiFromHook = useApiClient();
  const apiRef = useRef(apiFromHook); // stable reference to avoid effect loops
  if (apiRef.current !== apiFromHook) {
    // in case provider recreates the client, keep it fresh without causing re-fetch churn
    apiRef.current = apiFromHook;
  }

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(null);

  // Use a deterministic signature for filters to avoid accidental re-fetches
  const filtersSignature = JSON.stringify(filters ?? {});

  // Request id to ignore stale responses
  const reqIdRef = useRef(0);
  // Abort controller to actually cancel inflight requests (prevents late updates)
  const abortRef = useRef(null);

  useEffect(() => {
    let didUnmount = false;

    const fetchTickets = async () => {
      setLoading(true);
      setError(null);

      // Start a new request
      const myReqId = ++reqIdRef.current;
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      try {
        // sanitize "All" to ""
        const raw = JSON.parse(filtersSignature || "{}");
        const sanitizedFilters = Object.keys(raw).reduce((acc, key) => {
          acc[key] = raw[key] === "All" ? "" : raw[key];
          return acc;
        }, {});

        const res = await apiRef.current.get("/tickets", {
          params: { ...sanitizedFilters, pageSize, page: currentPage },
          signal: ac.signal,
        });

        // Ignore if a newer request has started or component unmounted
        if (didUnmount || myReqId !== reqIdRef.current) return;

        // Be resilient to different response shapes
        const content =
          Array.isArray(res?.data?.content)
            ? res.data.content
            : Array.isArray(res?.data)
            ? res.data
            : [];

        const pages =
          typeof res?.data?.totalPages === "number"
            ? res.data.totalPages
            : 1;

        setTickets(content);
        setTotalPages(pages);
        setError(null);
      } catch (err) {
        if(err.message.includes("403")){
          logoutUser();
          return;
        }
        if (didUnmount) return;
        if (err?.name === "AbortError") {
          // aborted due to a newer request; ignore silently
          return;
        }
        const errorMessage =
          err?.response?.data?.businessExceptionDescription ||
          "Failed to fetch tickets. Please try again later.";
        setError(errorMessage);
      } finally {
        if (didUnmount || reqIdRef.current === 0) return;
        if (myReqId === reqIdRef.current) {
          setLoading(false);
          setMainRefresh(false);
          setExecuteFilter(false);
        }
      }
    };

    if (executeFilter) {
      fetchTickets();
    }

    return () => {
      didUnmount = true;
      abortRef.current?.abort();
    };
  }, [
    executeFilter,
    filtersSignature,
    pageSize,
    currentPage,
    setExecuteFilter,
    setMainRefresh,
  ]);

  return {
    tickets,
    loading,
    totalPages,
    error,
  };
};

export default useTicketsTableData;
